/**
 * THE TWO SENTENCES that describe what a status override does to the item
 * receipts — one per direction. `deliveredTickNotice` for an override that
 * CREATES receipts (`→ delivered`), `receiptClearNotice` for one that DESTROYS
 * the tick on them (`→ submitted`/`approved`/`rejected`/`ordered`).
 *
 * 📝 **THE FILENAME IS NARROWER THAN THE MODULE SINCE 2026-09-19, AND THAT IS
 * A DELIBERATE CHOICE, NOT AN OVERSIGHT.** The second sentence belongs beside
 * the first — they are opposite readings of one control, they share the
 * pluraliser and the `Number(is_received) !== 1` predicate below, and a retune
 * of either wording must SEE the other or the two drift into contradicting each
 * other in one dialog. Renaming the file would touch three call sites, this
 * file's test and two topic-file references for no behavioural gain; proposed
 * as a separate change, not smuggled into this one.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `deliveredTickNotice` — the `→ delivered` direction
 * ───────────────────────────────────────────────────────────────────────────
 *
 * THE ONE SENTENCE that warns a user an override to `delivered` will tick every
 * item off as received.
 *
 * Owner ruling, 2026-09-12: *"I think if the order is overridden to say
 * delivered it should auto tick all the items to say delivered. The warning for
 * the override should tell the user that moving the order to delivered will tick
 * all items off as being delivered"*.
 *
 * 🔑 IT DESCRIBES A WRITE THAT REALLY HAPPENS. `PATCH /api/orders/:id/status`
 * marks every not-already-received item received when the target is `delivered`
 * and differs from the current status, recording the caller as `received_by`
 * (`server/tests/api/order-status-override-ticks-items.test.js`). A warning about
 * a write the server does not make is worse than no warning at all.
 *
 * 🔀 EXTRACTED, NOT COPIED. There are THREE confirms in `client/src` that can
 * carry an order to `delivered` — `components/InlineStatusJump.js` (the Override
 * dropdown, on Department Orders AND My Orders) and BOTH of `pages/OrderPage.js`'s
 * (`handleMarkDelivered` and the page's status `<select>`). Writing the sentence
 * out three times is exactly what the Code Unification Principle refuses: the
 * next retune of the wording would land on one of them and the other two would
 * silently keep the old promise.
 *
 * 🪤 THE NUMBER IS READ, NEVER GUESSED — from `order.items` and nothing else.
 * Every call site passes the body of `GET /api/orders/:id`, which carries the
 * full items array. When it is absent the sentence DROPS THE NUMBER rather than
 * substituting `item_count` or `delivery_info.total_items`: a count from a
 * different query can disagree with what the server is about to write, and a
 * prompt that over- or under-states it is worse than one that says "all items".
 *
 * 🔴 **IT COUNTS THE OUTSTANDING ITEMS, NOT THE ITEMS — CORRECTED 2026-09-12 BY
 * ADVERSARIAL REVIEW, AND THE FIRST VERSION WAS WRONG IN THE ONE CASE THE
 * FEATURE EXISTS TO PROTECT.** It said `items.length`, so a
 * `partially_delivered → delivered` jump on an order with 20 of 29 already
 * received by Stores promised *"This will mark all 29 items as received by
 * you"* — while the server writes 9 and leaves the other 20 with THEIR OWN
 * receiver and date untouched (the `(is_received IS NULL OR is_received != 1)`
 * arm, pinned by §1.3 of
 * `server/tests/api/order-status-override-ticks-items.test.js`). The prompt was
 * claiming credit for twenty receipts somebody else recorded. The predicate here
 * is the JavaScript twin of the server's WHERE clause, deliberately:
 * `Number(is_received) !== 1` treats `0`, `null` and a missing key alike, exactly
 * as `IS NULL OR != 1` does.
 *
 * 🪤 **THE NONE-OUTSTANDING FORM DROPS "by you", and that is not a style
 * choice**: when every item is already received the server writes NOTHING
 * (`deliveredTickCount > 0` gates the UPDATE), so a sentence attributing a
 * receipt to the caller would be false. It states the position instead.
 *
 * ⚠️ **This trusts `is_received` to be PRESENT on the rows.** It is: `GET
 * /api/orders/:id` selects `oi.*`, and `is_received` is not in
 * `ACCOUNTING_ITEM_FIELDS` (`server/routes/orders.js:242`), so
 * `stripAccountingItemFields` leaves it on the row for every viewer. If a caller
 * ever passes a THINNED item shape (the `preview_items` allowlist, say), every
 * row reads outstanding and the prompt over-states — which is the old bug, back
 * through a different door. Pass the detail row, not a preview row.
 *
 * @param {Object} order the order the confirm is about
 * @returns {string} the sentence, with NO leading blank line — callers append it
 *   to their own prompt with whatever separator that prompt already uses.
 */
// A confirmation prompt is read by a human, so "all 1 items" is not
// acceptable — the same reason the Override prompt carries the order NUMBER
// (2026-08-12). Module-level since 2026-09-19 so both sentences share it; it
// was function-local while there was only one.
import { deliveryCountableItems } from './deliveryCountableItems';

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

/** The server's `(is_received IS NULL OR is_received != 1)`, in JavaScript. */
const isOutstanding = (i) => Number(i && i.is_received) !== 1;

export function deliveredTickNotice(order) {
  const items = order && order.items;
  if (!Array.isArray(items)) return 'This will mark all items as received by you.';

  // ⚖️ GOODS ONLY (owner ruling 2026-09-19). This sentence is the confirm prompt
  // for "mark everything received", and the server's tick-all UPDATE is now
  // scoped `AND adjusts_item_id IS NULL` — so counting adjustment rows here
  // would promise a number of rows the write will NOT touch. The prompt and the
  // statement it describes have to agree about the same set.
  const countable = deliveryCountableItems(items);
  const total = countable.length;
  const outstanding = countable.filter(isOutstanding).length;
  const already = total - outstanding;

  // An order with NO items at all. The server writes nothing and its audit line
  // reads `0 of 0 items marked received`; "All 0 items are already marked
  // received" would be true and unreadable, so this says the position plainly.
  // Not one of the four forms the review specified — it did not arise there, and
  // a fifth honest sentence beats a fourth unreadable one.
  if (total === 0) return 'This order has no items to mark received.';

  if (outstanding === 0) {
    return `All ${plural(total, 'item')} ${total === 1 ? 'is' : 'are'} already marked received.`;
  }
  if (already === 0) {
    return `This will mark all ${plural(total, 'item')} as received by you.`;
  }
  return `This will mark the ${plural(outstanding, 'outstanding item')} as received by you (${already} already received).`;
}

/**
 * ───────────────────────────────────────────────────────────────────────────
 * `receiptClearNotice` — the `→ submitted`/`approved`/`rejected`/`ordered`
 * direction. Added 2026-09-19.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * THE ONE SENTENCE that warns a user an override to a pre-delivery status will
 * clear the delivered ticks.
 *
 * Owner ruling, 2026-09-19: *"an order being sent to 'ordered' or any steps
 * before should clear the delivered tick boxes I think. The log should still
 * show that its was ticked by someone, then status was overidden by the other
 * person"*.
 *
 * 🔑 IT DESCRIBES A WRITE THAT REALLY HAPPENS, AND IT NAMES BOTH HALVES OF IT.
 * `PATCH /api/orders/:id/status` with `reset_side_state` clears `is_received`
 * on every item when the target is in `TARGETS_NEEDING_ITEM_RESET`, and — since
 * the same ruling — PRESERVES `received_date` / `received_by` and records who
 * was overridden in `order_audit_log.details`. The second half is not decoration:
 * a user asked to destroy somebody's receipt is owed the fact that the receipt
 * is recorded rather than erased, and that is precisely what the owner's second
 * sentence asks for.
 *
 * 🔻 IT REPLACES A SENTENCE THAT WAS FALSE, AND THE FALSEHOOD IS WHY THE
 * WORDING IS SPLIT THE WAY IT IS. The old backward text promised *"This will
 * clear all item-delivered flags and reset the approval chain to a fresh
 * state."* on EVERY backward jump. Measured over the component's own
 * `isBackwardJump`: nine backward pairs reach the wipe, only four of them
 * target `submitted`, and the chain arm is gated on `status === 'submitted'`
 * AND `order.department_id` AND an existing `order_approvals` row — which every
 * My Space order raised since 2026-09-11 lacks. So the chain half was false or
 * unprovable on 9 of 9. **The chain clause is therefore NOT in this sentence at
 * all**: the caller appends it, on the one target where it can fire. A sentence
 * true on one branch and false on another is the exact defect this repo already
 * shipped once in the matching audit line.
 *
 * 🪤 IT COUNTS THE TICKED ITEMS, NOT THE ITEMS — the mirror image of
 * `deliveredTickNotice`'s own correction, and wrong in the same way if copied
 * carelessly. "This will clear the delivered tick on 29 items" on an order
 * where Stores ticked 9 overstates the loss by twenty; worse, on an order with
 * NOTHING ticked it warns about destroying a receipt that does not exist, which
 * trains the user to dismiss the dialog.
 *
 * 🪤 THE NUMBER IS READ FROM `order.items` AND NOTHING ELSE, same rule and same
 * reason as the sentence above: `item_count` and `delivery_info.total_items`
 * come from different queries and can disagree with what the server writes.
 * Absent, the sentence drops the number rather than inventing one. **Pass the
 * DETAIL row, never a `preview_items` row** — a thinned shape would read as
 * nothing ticked and suppress the warning entirely, which is the failure that
 * matters here (the delivered sentence's thinned-shape failure is merely an
 * overstatement).
 *
 * @param {Object} order the order the confirm is about
 * @returns {string} the sentence, with NO leading blank line — callers append
 *   it to their own prompt with whatever separator that prompt already uses.
 */
export function receiptClearNotice(order) {
  const items = order && order.items;
  if (!Array.isArray(items)) {
    return 'This will clear the delivered tick on every item. The audit log records who ticked them, and that you overrode it.';
  }

  const total = items.length;
  const ticked = items.filter((i) => !isOutstanding(i)).length;

  // Nothing is ticked, so nothing is destroyed. Saying so plainly is better
  // than silence: the user picked a target that CAN clear receipts, and "this
  // order had none" is the answer to the question they were about to ask.
  // It is also what keeps the warning credible on the pairs where it fires
  // harmlessly (`submitted → approved`, say).
  if (ticked === 0) return 'No items are ticked as delivered, so no receipt will be cleared.';

  const them = ticked === 1 ? 'it' : 'them';
  const scope = ticked === total
    ? `all ${plural(total, 'item')}`
    : `${ticked} of ${plural(total, 'item')}`;
  return `This will clear the delivered tick on ${scope}. The audit log records who ticked ${them}, and that you overrode it.`;
}

export default deliveredTickNotice;
